import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { useKioskSign, type KioskSignResult } from '@/hooks/use-kiosk-sign';
import { SignaturePad } from '@/components/safety/signature-pad';
import { OfflineSignatureIndicator } from '@/app/components/offline/offline-signature-indicator';
import {
  JSAHazardCarousel,
  type CarouselHazard,
  type HazardAcknowledgement,
  type HazardAcknowledgementProgress,
} from '@/components/safety/jsa-hazard-carousel';
import {
  PlanInitialsFields,
  type PlanInitialRevision,
} from '@/app/components/safety/plan-initials-fields';
import type { PlanInitialInput } from '@/lib/types';

export type { KioskSignResult };

interface KioskSignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jsaId: number;
  signatureLineId: number;
  hazards?: CarouselHazard[];
  plans?: PlanInitialRevision[];
  timeZone?: string;
  onSuccess: (result: KioskSignResult) => void;
  pinMode?: boolean;
}

export function KioskSignModal({
  open,
  onOpenChange,
  jsaId,
  signatureLineId,
  hazards = [],
  plans = [],
  timeZone = 'UTC',
  onSuccess,
  pinMode = false,
}: KioskSignModalProps) {
  const usernameRef = useRef<HTMLInputElement>(null);
  const [acknowledgementData, setAcknowledgementData] = useState<HazardAcknowledgement[] | null>(
    hazards.length === 0 ? [] : null
  );
  const [ackProgress, setAckProgress] = useState<HazardAcknowledgementProgress>({
    acknowledged: 0,
    total: hazards.length,
  });
  const [planInitials, setPlanInitials] = useState<PlanInitialInput[]>([]);
  const {
    usePinField,
    username,
    setUsername,
    credential,
    setCredential,
    signatureData,
    setSignatureData,
    credentialsComplete,
    error,
    submitting,
    handleSubmit,
    clear,
  } = useKioskSign({
    pinMode,
    documentId: jsaId,
    signatureLineId,
    endpoint: `/api/v1/safety/jsas/${jsaId}/kiosk-sign/`,
    bodyBase: {
      signature_line_id: signatureLineId,
      acknowledgement_data: acknowledgementData,
      plan_initials: planInitials,
    },
    onSuccess: (result) => {
      onSuccess(result);
      onOpenChange(false);
      setAcknowledgementData(hazards.length === 0 ? [] : null);
      setPlanInitials([]);
    },
  });

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      clear();
      setSignatureData('');
      setAcknowledgementData(hazards.length === 0 ? [] : null);
      setPlanInitials([]);
    }
    onOpenChange(nextOpen);
  };

  // Reset the acknowledgement state whenever the modal is opened so each
  // kiosk signing session starts fresh and cannot reuse a previous signer's
  // acknowledgement.
  useEffect(() => {
    if (open) {
      setSignatureData('');
      setAcknowledgementData(hazards.length === 0 ? [] : null);
      setPlanInitials([]);
    }
  }, [open, hazards.length, signatureLineId, setSignatureData]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md z-[60] flex max-h-[90vh] max-sm:max-h-[calc(100vh-8rem)] flex-col overflow-hidden max-sm:top-4 max-sm:translate-y-0"
        onOpenAutoFocus={(e) => {
          // Prevent Radix's aggressive auto-focus which races with the mobile
          // keyboard and can garble input when the user types quickly.
          e.preventDefault();
          requestAnimationFrame(() => usernameRef.current?.focus());
        }}
      >
        <DialogHeader>
          <DialogTitle>Kiosk Sign</DialogTitle>
          <DialogDescription>
            {usePinField
              ? 'Enter your username and signing PIN, then sign to complete this JSA.'
              : 'Enter your credentials and sign to complete this JSA. Your login will not affect the current supervisor session.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <OfflineSignatureIndicator />
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="kiosk-username">Username</Label>
              <Input
                ref={usernameRef}
                id="kiosk-username"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                disabled={submitting}
              />
            </div>

            {usePinField ? (
              <div className="space-y-2">
                <Label htmlFor="kiosk-pin">6-Digit PIN</Label>
                <Input
                  id="kiosk-pin"
                  type="tel"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  autoComplete="off"
                  value={credential}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCredential(v);
                  }}
                  placeholder="Enter your 6-digit PIN"
                  disabled={submitting}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="kiosk-password">Password</Label>
                <Input
                  id="kiosk-password"
                  type="password"
                  autoComplete="off"
                  value={credential}
                  onChange={(e) => setCredential(e.target.value)}
                  placeholder="Enter your password"
                  disabled={submitting}
                />
              </div>
            )}

            {hazards.length > 0 && (
              <div className="space-y-2">
                <Label>Acknowledge each hazard</Label>
                <JSAHazardCarousel
                  hazards={hazards}
                  onComplete={setAcknowledgementData}
                  onProgress={setAckProgress}
                />
              </div>
            )}

            <PlanInitialsFields
              plans={plans}
              values={planInitials}
              onChange={setPlanInitials}
              disabled={submitting}
              timeZone={timeZone}
            />

            <div className="space-y-2">
              <Label>Signature</Label>
              <SignaturePad
                onAccept={setSignatureData}
                onClear={() => setSignatureData('')}
                acceptDisabled={!credentialsComplete || !acknowledgementData}
                width={400}
                height={120}
              />
            </div>
          </div>

          <DialogFooter className="pt-4 sm:flex-col sm:items-end sm:space-x-0 gap-2">
            {hazards.length > 0 && !acknowledgementData && (
              <p className="text-sm text-amber-700 w-full text-right" data-testid="sign-blocked-hazards">
                Review each hazard to continue ({ackProgress.acknowledged} of {ackProgress.total} acknowledged)
              </p>
            )}
            <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !credentialsComplete || !signatureData || !acknowledgementData}>
              {submitting ? 'Signing...' : 'Sign'}
            </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
